/**
 * MapView (UI-SPEC W1) — the WebGL map canvas with overlay chrome.
 *
 * Architecture (per CLAUDE.md hard constraint + D-28 + D-68):
 *  - All ~2,500 pins render via a SINGLE MapLibre symbol layer.
 *  - Sprite atlas is at /sprites/storm@2x.{png,json} (Plan 02 emitted these).
 *  - Data-driven `icon-image`: ['concat', ['get','shape'], '-', ['get','active_color']]
 *  - `cluster: false` on the GeoJSON source — explicit no-clustering canary.
 *  - NEVER `maplibregl.Marker` (DOM markers are forbidden by D-28).
 *
 * Tile-fallback flip (D-82):
 *  - Primary: OpenFreeMap "liberty" style (loaded via the Map's `style:` URL).
 *  - Fallback: PMTiles archive served from /tiles/storm.pmtiles (D-81).
 *    The fallback ships with a single background-fill layer in Phase 3 dev;
 *    production will mirror richer OFM equivalents via Phase 7 release prep.
 *
 * Test hooks (window.__map / window.__openAssetPanel):
 *  - Playwright assertions need to inspect the source's feature count + force
 *    a synthetic 'error' event for the fallback flip path. We expose the map
 *    instance + a "open panel for asset id" entry point only in tests.
 *  - These are not used by application code; the existence of `__map` does
 *    not change behavior of any non-test code path.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import maplibregl, { Map as MaplibreMap, GeoJSONSource } from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import { ensurePMTilesProtocol, OFM_STYLE_URL, pmtilesUrl } from "./style";
import { registerStormSprites } from "./loadStormSprites";
import { withActiveColor, type Dimension } from "./pinSource";
import { createTileFallbackController, type TileFallbackController } from "./tileFallback";
import { useAssetGeoJson, type AssetFeature } from "../../api/assets";
import { useAssetStateStream } from "./useAssetStateStream";
import { TileFallbackBanner } from "./TileFallbackBanner";
import { DimensionPillToggleGroup } from "./DimensionPillToggleGroup";
import { LabelToggleButton } from "./LabelToggleButton";
import { BasemapToggleButton } from "./BasemapToggleButton";
import { MapEmptyState } from "./MapEmptyState";
import { AssetSidePanel } from "./AssetSidePanel";
import { Link } from "react-router-dom";
import { FormattedMessage } from "react-intl";

const SYMBOL_LAYER_ID = "asset-pins";
const LABEL_LAYER_ID = "asset-labels";
const ASSETS_SOURCE_ID = "assets";
const PMTILES_SOURCE_ID = "pmtiles-fallback";
const PMTILES_BG_LAYER_ID = "pmtiles-fallback-bg";
// Selection halo — red-600 (#DC2626) ring. 2026-05-18 hotfix rebrand
// from forest_green #406020 (Phase 11 D-04) after Z2450 UAT showed the
// green ring visually blended into ok-state green pins. Red sits in a
// hue family disjoint from all four pin colors (ok/warning/error/neutral),
// so the ring reads as chrome rather than as part of the pin. Picked
// red-600 over red-500 #EF4444 (the error-pin color) so the ring stays
// distinct against error pins via value contrast. Mirrors the Android
// storm_map_style.json `selected-pin-ring` layer.
const SELECTED_SOURCE_ID = "selected-asset";
const SELECTED_RING_LAYER_ID = "selected-pin-ring";

// 2026-05-03 UAT bug #2 — ESRI World Imagery raster basemap. Free,
// attribution-required, no API key. The layer is inserted BELOW the asset
// symbol layer so pins always render above imagery; visibility flips on the
// basemap toggle. Mirrors the Android storm_map_style.json `esri-imagery`
// source + `esri-imagery-layer`.
const ESRI_IMAGERY_SOURCE_ID = "esri-imagery";
const ESRI_IMAGERY_LAYER_ID = "esri-imagery-layer";
const ESRI_IMAGERY_TILE_URL =
  "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}";
const ESRI_IMAGERY_ATTRIBUTION =
  "Tiles &copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community";

const EMPTY_FC = { type: "FeatureCollection" as const, features: [] };

const STORM_LAST_CENTER_KEY = "storm.last_map_center";
const STORM_PIN_LABELS_KEY = "storm-pin-labels";
const STORM_BASEMAP_KEY = "storm-basemap";

type Basemap = "carto" | "satellite";

function readInitialBasemap(): Basemap {
  if (typeof window === "undefined") return "carto";
  try {
    const raw = window.localStorage.getItem(STORM_BASEMAP_KEY);
    return raw === "satellite" ? "satellite" : "carto";
  } catch {
    return "carto";
  }
}

interface PersistedCenter {
  lat: number;
  lng: number;
  zoom: number;
}

function readPersistedCenter(): PersistedCenter | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(STORM_LAST_CENTER_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<PersistedCenter>;
    if (
      typeof parsed.lat === "number" &&
      typeof parsed.lng === "number" &&
      typeof parsed.zoom === "number"
    ) {
      return { lat: parsed.lat, lng: parsed.lng, zoom: parsed.zoom };
    }
    return null;
  } catch {
    return null;
  }
}

function readInitialLabelsVisible(): boolean {
  if (typeof window === "undefined") return true;
  try {
    const raw = window.localStorage.getItem(STORM_PIN_LABELS_KEY);
    if (raw === null) return true; // default ON
    return raw === "on";
  } catch {
    return true;
  }
}

 
type OpenPanelFn = (assetId: number) => void;
 
type OpenSyntheticPanelFn = (feature: AssetFeature) => void;

// Phase 11 D-08 + Plan 11-01a Task 2 — test-only `window.__triggerRecolor`
// hook. Drives the dimension state setter (`setActiveDim`) from Playwright so
// the pin-recolor perf gate (`web/tests/perf/pin-recolor.spec.ts`) can measure
// the production hot path (`src.setData(withActiveColor(...))`) without
// pixel-perfect canvas clicks. Gated by `process.env.NODE_ENV !== "production"`
// so production bundles dead-code-eliminate the assignment via Vite tree-shaking
// (verified by acceptance criterion `grep -c __triggerRecolor web/dist/assets/*.js`
// returning 0; T-11-01 mitigation in Phase 11 threat model).
//
 
type TriggerRecolorFn = (d: Dimension) => void;

interface SyntheticPanelHook {
  __openAssetPanel?: OpenPanelFn;
  __openSyntheticPanel?: OpenSyntheticPanelFn;
  __map?: MaplibreMap;
  __triggerRecolor?: TriggerRecolorFn;
}

export function MapView() {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<MaplibreMap | null>(null);
  const fallbackCtrlRef = useRef<TileFallbackController | null>(null);
  const [mapReady, setMapReady] = useState(false);
  const [activeDim, setActiveDim] = useState<Dimension>("cleaning");
  const [labelsVisible, setLabelsVisible] = useState<boolean>(readInitialLabelsVisible);
  const [basemap, setBasemap] = useState<Basemap>(readInitialBasemap);
  const [selectedAssetId, setSelectedAssetId] = useState<number | null>(null);
  const [syntheticFeature, setSyntheticFeature] = useState<AssetFeature | null>(null);
  const [fallbackActive, setFallbackActive] = useState(false);
  const [pickMode, setPickMode] = useState(false);
  const [pickedCoords, setPickedCoords] = useState<{ lat: number; lng: number } | null>(null);
  const [ghostPin, setGhostPin] = useState<{ x: number; y: number } | null>(null);

  const { data: geoJson, isLoading } = useAssetGeoJson();
  // Phase 4 Plan 05 — SSE-driven invalidation for the GeoJSON cache.
  // The hook owns the EventSource lifecycle (subscribe on mount, close on
  // unmount) and dispatches `qc.invalidateQueries` on each `state_changed`
  // event so the map recolors within the NFR-PERF-3 budget (≤ 2 s p95).
  // Status fields are reserved for an optional connection indicator.
  useAssetStateStream();

  // Mount the map exactly once — React StrictMode in dev double-invokes the
  // effect, but the `mapRef.current` guard prevents a second `Map` instance.
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    ensurePMTilesProtocol();

    const persisted = readPersistedCenter();
    const initialCenter: [number, number] = persisted
      ? [persisted.lng, persisted.lat]
      : [43.9736, 26.0843]; // Unayzah, Al-Qassim, Saudi Arabia (target municipality)
    const initialZoom = persisted?.zoom ?? 12;

    const map = new maplibregl.Map({
      container: containerRef.current,
      style: OFM_STYLE_URL,
      center: initialCenter,
      zoom: initialZoom,
      attributionControl: { compact: false },
    });
    mapRef.current = map;

    // Register the storm sprite atlas BEFORE any layer references its keys.
    // The listener resolves `manhole-green`/`catchbasin-yellow`/... lazily as
    // MapLibre fires `styleimagemissing`. Required because the OFM Liberty
    // base style does not include our atlas (Phase 3 left this gap).
    registerStormSprites(map);

    // Test hook (Playwright). Not used by app code.
    (window as unknown as SyntheticPanelHook).__map = map;

    const fallbackCtrl = createTileFallbackController({
      onFallbackFlip: setFallbackActive,
    });
    fallbackCtrlRef.current = fallbackCtrl;

    map.on("load", () => {
      // Capture OFM-derived layer ids BEFORE we add our own — the tile
      // fallback toggles visibility on these.
      const ofmLayerIds = map.getStyle().layers.map((l) => l.id);

      // Add the PMTiles fallback source + a minimal background layer
      // (visibility=none until the fallback flip kicks in).
      try {
        map.addSource(PMTILES_SOURCE_ID, {
          type: "vector",
          url: `pmtiles://${pmtilesUrl()}`,
        });
      } catch {
        // PMTiles archive may not exist in dev; the source-add itself doesn't
        // fetch, but if it does and 404s, the error is handled by the
        // tileFallback controller (which is what we want).
      }
      map.addLayer({
        id: PMTILES_BG_LAYER_ID,
        type: "background",
        layout: { visibility: "none" },
        paint: { "background-color": "#F0EDE5" }, // chrome paint - brand surface-variant (--color-surface-variant in tokens.css; muted neutral so PMTiles fallback reads as system chrome, not data)
      });

      // 2026-05-03 UAT bug #2 — ESRI World Imagery raster source + layer.
      // Inserted BEFORE the asset-pins symbol layer so pins always render
      // above imagery. Initial visibility=none; toggled by the basemap
      // effect below.
      map.addSource(ESRI_IMAGERY_SOURCE_ID, {
        type: "raster",
        tiles: [ESRI_IMAGERY_TILE_URL],
        tileSize: 256,
        // ESRI World Imagery's tile pyramid only goes to z19 globally. Without
        // maxzoom MapLibre defaults to 22 and fetches z20+ tiles that 404,
        // surfacing as "map data not yet available". Capping at 19 makes
        // MapLibre overzoom (scale up) the z19 tile past zoom 19 instead.
        maxzoom: 19,
        attribution: ESRI_IMAGERY_ATTRIBUTION,
      });
      map.addLayer({
        id: ESRI_IMAGERY_LAYER_ID,
        type: "raster",
        source: ESRI_IMAGERY_SOURCE_ID,
        layout: { visibility: "none" },
      });

      // Add the asset GeoJSON source (no clustering — D-28 hard rule).
      map.addSource(ASSETS_SOURCE_ID, {
        type: "geojson",
        data: { type: "FeatureCollection", features: [] },
        cluster: false,
      });

      // Symbol layer: data-driven icon-image picks the sprite based on
      // shape + active_color (the recoloring is done client-side via
      // setData; see pinSource.withActiveColor).
      map.addLayer({
        id: SYMBOL_LAYER_ID,
        type: "symbol",
        source: ASSETS_SOURCE_ID,
        layout: {
          "icon-image": [
            "concat",
            ["get", "shape"],
            "-",
            ["get", "active_color"],
          ],
          "icon-size": 1, // sprite manifests describe logical 24px (D-69)
          "icon-allow-overlap": true,
          "icon-ignore-placement": true,
        },
      });

      // Number-label layer (separate symbol layer; toggled via
      // setLayoutProperty for the visibility flip).
      //
      // Phase 7 finding (live-deploy probe) — `text-font` MUST be specified.
      // MapLibre's default is `["Open Sans Regular", "Arial Unicode MS Regular"]`
      // and OpenFreeMap (our primary tile + glyph host) does NOT serve those
      // font stacks: every range fetch returns 404, no labels render, console
      // floods with "Unable to load glyph range … Rendering codepoint locally".
      // OFM's liberty style references only Noto Sans variants, so we align
      // with that — `Noto Sans Regular` returns 200 from OFM's glyph endpoint.
      map.addLayer({
        id: LABEL_LAYER_ID,
        type: "symbol",
        source: ASSETS_SOURCE_ID,
        layout: {
          "text-field": ["get", "number"],
          "text-font": ["Noto Sans Regular"],
          "text-anchor": "top",
          "text-offset": [0, 0.9],
          "text-size": 11,
          "text-allow-overlap": false,
          visibility: labelsVisible ? "visible" : "none",
        },
        paint: {
          "text-color": "#1A1A1A", // chrome paint - brand text-primary (--color-text-primary in tokens.css)
          "text-halo-color": "#FFFFFF", // chrome paint - brand surface-raised (--color-surface-raised in tokens.css)
          "text-halo-width": 1.5,
        },
      });

      // Selection-halo source + layer (drawn above asset-pins + asset-labels
      // so the ring sits visually on top of the sprite).
      map.addSource(SELECTED_SOURCE_ID, {
        type: "geojson",
        data: EMPTY_FC,
        cluster: false,
      });
      map.addLayer({
        id: SELECTED_RING_LAYER_ID,
        type: "circle",
        source: SELECTED_SOURCE_ID,
        paint: {
          "circle-radius": 16,
          "circle-color": "rgba(0,0,0,0)",
          "circle-stroke-color": "#DC2626", // chrome paint - brand-exempt selection halo red-600: deliberately a literal, NOT a token (a token would pull it into V-CP1's chrome-saturation scope; 2026-05-18 green-pin hue-collision hotfix)
          "circle-stroke-width": 3,
        },
      });

      // Wire the symbol-layer click handler.
      map.on("click", SYMBOL_LAYER_ID, (e) => {
        const feat = e.features?.[0];
        if (!feat) return;
        const id = feat.properties?.id as number | undefined;
        if (typeof id === "number") {
          setSelectedAssetId(id);
          setSyntheticFeature(null);
        }
      });
      // Cursor hint over pins.
      map.on("mouseenter", SYMBOL_LAYER_ID, () => {
        map.getCanvas().style.cursor = "pointer";
      });
      map.on("mouseleave", SYMBOL_LAYER_ID, () => {
        map.getCanvas().style.cursor = "";
      });

      // Attach the fallback controller now that we know which layers belong
      // to OFM vs PMTiles.
      fallbackCtrl.attach(map, ofmLayerIds, [PMTILES_BG_LAYER_ID]);

      setMapReady(true);
    });

    // Persist the last-seen camera position (debounced to map idle).
    let idleTimer: ReturnType<typeof setTimeout> | null = null;
    map.on("idle", () => {
      if (idleTimer) clearTimeout(idleTimer);
      idleTimer = setTimeout(() => {
        const c = map.getCenter();
        try {
          window.localStorage.setItem(
            STORM_LAST_CENTER_KEY,
            JSON.stringify({ lat: c.lat, lng: c.lng, zoom: map.getZoom() }),
          );
        } catch {
          // localStorage may be unavailable (private mode); silently skip.
        }
      }, 500);
    });

    return () => {
      if (idleTimer) clearTimeout(idleTimer);
      fallbackCtrlRef.current?.dispose();
      fallbackCtrlRef.current = null;
      try {
        map.remove();
      } catch {
        // ignore
      }
      mapRef.current = null;
      delete (window as unknown as SyntheticPanelHook).__map;
    };
  }, []); // mount-once intentional; deps reference module-level constants

  // Update GeoJSON source when query data changes — apply the active dim's
  // color as `active_color` for the icon-image expression.
  useEffect(() => {
    if (!mapReady || !geoJson) return;
    const m = mapRef.current;
    if (!m) return;
    const src = m.getSource(ASSETS_SOURCE_ID) as GeoJSONSource | undefined;
    if (!src) return;
    src.setData(withActiveColor(geoJson, activeDim));
  }, [mapReady, geoJson, activeDim]);

  // Toggle label-layer visibility + persist.
  useEffect(() => {
    if (!mapReady) return;
    const m = mapRef.current;
    if (!m) return;
    try {
      m.setLayoutProperty(LABEL_LAYER_ID, "visibility", labelsVisible ? "visible" : "none");
    } catch {
      // ignore
    }
    try {
      window.localStorage.setItem(STORM_PIN_LABELS_KEY, labelsVisible ? "on" : "off");
    } catch {
      // ignore
    }
  }, [mapReady, labelsVisible]);

  // 2026-05-03 UAT bug #2 — toggle ESRI imagery layer visibility + persist.
  // Note: we deliberately do NOT hide the OFM cartography layers when
  // satellite is on. The imagery is fully opaque so OFM is visually
  // occluded; leaving OFM declared keeps the existing tileFallback
  // controller's visibility ownership clean (it captured the OFM ids at
  // load and toggles them on PMTiles fallback). If the two ever conflict
  // — e.g. tile fallback flips while satellite is on — the satellite
  // layer continues to draw on top, which is the correct behavior.
  useEffect(() => {
    if (!mapReady) return;
    const m = mapRef.current;
    if (!m) return;
    try {
      m.setLayoutProperty(
        ESRI_IMAGERY_LAYER_ID,
        "visibility",
        basemap === "satellite" ? "visible" : "none",
      );
    } catch {
      // Layer may not exist yet on first effect run if the load handler
      // hasn't completed; the second effect run (mapReady=true) covers it.
    }
    try {
      window.localStorage.setItem(STORM_BASEMAP_KEY, basemap);
    } catch {
      // localStorage may be unavailable (private mode); silently skip.
    }
  }, [mapReady, basemap]);

  // Test hooks: programmatic open of the side panel + dimension recolor,
  // used by Playwright in lieu of pixel-perfect canvas clicks.
  //
  // FIND-15-011 (2026-05-11): `__openAssetPanel` + `__openSyntheticPanel`
  // were previously NOT gated and shipped in production bundles. A malicious
  // call from the browser console with bad argument shapes would crash the
  // React tree (the 2026-05-11 PM web walk surfaced this when its probe
  // called `__openSyntheticPanel(1)` instead of a real AssetFeature — the
  // hook accessed `feature.properties.id` on a number and threw, which then
  // re-rendered AssetSidePanel with the bad value and crashed there too).
  // Aligning ALL three hooks behind the same `process.env.NODE_ENV !==
  // "production"` gate matches the Phase 11 Plan 11-01a T-11-01 mitigation
  // pattern (Vite tree-shakes the entire assignment + clear out of the
  // production bundle).
  useEffect(() => {
    const hook = window as unknown as SyntheticPanelHook;
    if (process.env.NODE_ENV !== "production") {
      hook.__openAssetPanel = (assetId: number) => {
        setSelectedAssetId(assetId);
        setSyntheticFeature(null);
      };
      hook.__openSyntheticPanel = (feature: AssetFeature) => {
        setSyntheticFeature(feature);
        setSelectedAssetId(feature.properties.id);
      };
      hook.__triggerRecolor = (d: Dimension) => {
        setActiveDim(d);
      };
    }
    return () => {
      // Cleanup is also gated so Vite tree-shakes the entire hook surface
      // (assignment + clear) from production bundles. Verified by the
      // acceptance criterion `grep -cE '__openAssetPanel|__openSyntheticPanel|__triggerRecolor' web/dist/assets/*.js`
      // returning 0 (matching T-11-01 mitigation pattern).
      if (process.env.NODE_ENV !== "production") {
        hook.__openAssetPanel = undefined;
        hook.__openSyntheticPanel = undefined;
        hook.__triggerRecolor = undefined;
      }
    };
  }, []);

  // Resolve the selected feature: either a synthetic one (test injection,
  // e.g. a soft-deleted asset that the GeoJSON endpoint excludes) or one
  // from the current GeoJSON.
  const selectedFeature = useMemo<AssetFeature | null>(() => {
    if (syntheticFeature) return syntheticFeature;
    if (selectedAssetId === null || !geoJson) return null;
    return geoJson.features.find((f) => f.properties.id === selectedAssetId) ?? null;
  }, [syntheticFeature, selectedAssetId, geoJson]);

  // Push the selected feature to the halo source. Empty FC clears the ring
  // when the side panel closes (selectedAssetId → null).
  useEffect(() => {
    if (!mapReady) return;
    const m = mapRef.current;
    if (!m) return;
    const src = m.getSource(SELECTED_SOURCE_ID) as GeoJSONSource | undefined;
    if (!src) return;
    src.setData(
      selectedFeature
        ? { type: "FeatureCollection", features: [selectedFeature] }
        : EMPTY_FC,
    );
  }, [mapReady, selectedFeature]);

  // Pick-on-map: enter / exit / mousemove (ghost pin) / click handler.
  useEffect(() => {
    const m = mapRef.current;
    if (!m || !mapReady) return;
    if (!pickMode) {
      m.getCanvas().style.cursor = "";
      setGhostPin(null);
      return;
    }
    m.getCanvas().style.cursor = "crosshair";
    const onMapMove = (e: maplibregl.MapMouseEvent) => {
      setGhostPin({ x: e.point.x, y: e.point.y });
    };
    const onMapClick = (e: maplibregl.MapMouseEvent) => {
      setPickedCoords({ lat: e.lngLat.lat, lng: e.lngLat.lng });
      setPickMode(false);
      setGhostPin(null);
    };
    m.on("mousemove", onMapMove);
    m.once("click", onMapClick);
    return () => {
      try {
        m.off("mousemove", onMapMove);
        m.off("click", onMapClick);
      } catch {
        // ignore
      }
      m.getCanvas().style.cursor = "";
      setGhostPin(null);
    };
  }, [pickMode, mapReady]);

  // Empty-state guard: when geoJson is loaded and has zero features, do
  // NOT mount the map at all (UI-SPEC W6).
  if (!isLoading && geoJson && geoJson.features.length === 0) {
    return <MapEmptyState />;
  }

  return (
    <div className="relative w-full flex-1 min-h-[600px]">
      {/* MapLibre adds `.maplibregl-map` to this div which sets
          `position: relative`. We give it explicit width/height so the
          MapLibre canvas inside it has dimensions. The wrapper above is
          `relative + flex-1` so it grows to fill the main grid track. */}
      <div ref={containerRef} className="absolute inset-0 w-full h-full" data-testid="map-canvas" />

      {/* Top toolbar — chip strip + action buttons in one flex-wrap row.
          2026-05-17 FIND-B fix: previously two absolute-positioned divs
          (top-0 start-0 + top-0 end-0) collided at narrow widths because
          their absolute positioning ignored each other's bounds. Combined
          into one flex-wrap container so action buttons wrap below the
          chip strip when there's not enough horizontal space; chip strip
          itself scrolls horizontally inside its overflow container so the
          group never gets clipped by the action buttons. */}
      <div className="absolute pbs-4 ps-4 pe-4 z-10 top-0 inset-x-0 flex flex-wrap items-start justify-between gap-2 pointer-events-none">
        <div className="pointer-events-auto max-w-full overflow-x-auto">
          <DimensionPillToggleGroup
            value={activeDim}
            onChange={setActiveDim}
            disabled={!mapReady}
          />
        </div>
        <div className="pointer-events-auto flex gap-2 ms-auto">
          <Link
            to="/assets/import"
            data-testid="map-import-cta"
            className="inline-flex items-center justify-center h-9 ps-3 pe-3 rounded-md font-semibold text-sm bg-surface-raised text-on-surface ring-1 ring-divider hover:bg-surface focus-visible:ring-2 focus-visible:ring-primary transition-colors duration-200"
          >
            <FormattedMessage id="LABEL_IMPORT_ASSETS" />
          </Link>
          <LabelToggleButton
            visible={labelsVisible}
            onToggle={() => setLabelsVisible((v) => !v)}
          />
          <BasemapToggleButton
            satellite={basemap === "satellite"}
            onToggle={() =>
              setBasemap((b) => (b === "satellite" ? "carto" : "satellite"))
            }
          />
        </div>
      </div>

      {fallbackActive && (
        <TileFallbackBanner
          onRetryPrimary={() => fallbackCtrlRef.current?.retryPrimary()}
        />
      )}

      {/* Ghost-pin SVG overlay during pick-on-map mode (D-28: NOT a DOM
          marker — single SVG sibling to the canvas, transform-positioned
          via the `point` from MapLibre's mousemove event). */}
      {pickMode && ghostPin && (
        <svg
          data-testid="pick-on-map-ghost-pin"
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none z-20"
          width="100%"
          height="100%"
        >
          <circle
            cx={ghostPin.x}
            cy={ghostPin.y}
            r={10}
            fill="#406020" // chrome paint - brand primary (--color-primary in tokens.css)
            fillOpacity={0.55}
            stroke="#FFFFFF" // chrome paint - brand surface-raised (--color-surface-raised in tokens.css)
            strokeWidth={2}
          />
        </svg>
      )}

      {selectedFeature && (
        <AssetSidePanel
          feature={selectedFeature}
          onClose={() => {
            setSelectedAssetId(null);
            setSyntheticFeature(null);
            setPickMode(false);
            setPickedCoords(null);
          }}
          pickMode={pickMode}
          onEnterPickMode={() => setPickMode(true)}
          onExitPickMode={() => setPickMode(false)}
          pickedCoords={pickedCoords}
          onClearPickedCoords={() => setPickedCoords(null)}
        />
      )}
    </div>
  );
}
